import { useEffect, useState, useRef } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Upload, Check, Sparkles, AlertCircle, ChevronRight, ChevronLeft, ArrowRight, Share2, Download, Mail, Phone, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VisualizerPage = () => {
  const { shopName } = useParams();
  const [searchParams] = useSearchParams();
  const isEmbed = searchParams.get('embed') === 'true';
  
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [studioInfo, setStudioInfo] = useState(null);
  const [studioDetails, setStudioDetails] = useState(null);
  const [error, setError] = useState(null);
  
  // Visualizer state - with multi-step wrap selection
  const [step, setStep] = useState(1);
  const [wrapSelectionStep, setWrapSelectionStep] = useState(1); // 1: Brand, 2: Finish, 3: Color
  const [carImage, setCarImage] = useState(null);
  const [carImageBase64, setCarImageBase64] = useState('');
  const [masterWraps, setMasterWraps] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [selectedFinish, setSelectedFinish] = useState(null);
  const [selectedWrap, setSelectedWrap] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [resultImage, setResultImage] = useState(null);
  const [sampleCars, setSampleCars] = useState([]);
  const [showInquireModal, setShowInquireModal] = useState(false);
  const fileInputRef = useRef(null);

  // Available finishes
  const finishTypes = ['Gloss', 'Matte', 'Satin', 'Metallic', 'Brushed', 'Chrome', 'Carbon'];

  useEffect(() => {
    const checkAccess = async () => {
      try {
        const accessRes = await axios.get(`${API}/billing/studio-access/${shopName}`);
        
        if (accessRes.data.has_access) {
          setHasAccess(true);
          setStudioInfo(accessRes.data);
          
          // Get studio details for contact info
          const studioRes = await axios.get(`${API}/studios/public/${shopName}`).catch(() => ({ data: null }));
          setStudioDetails(studioRes.data);
          
          const [masterRes, mfrRes, carsRes] = await Promise.all([
            axios.get(`${API}/wraps/master-catalog`).catch(() => ({ data: [] })),
            axios.get(`${API}/wraps/manufacturers`).catch(() => ({ data: { manufacturers: [] } })),
            axios.get(`${API}/sample-cars`).catch(() => ({ data: { cars: [] } }))
          ]);
          
          setMasterWraps(masterRes.data);
          // Filter out manufacturers with 0 wraps
          const activeMfrs = mfrRes.data.manufacturers.filter(m => m.wrap_count > 0);
          setManufacturers(activeMfrs);
          setSampleCars(carsRes.data.cars || []);
        } else {
          setError('This visualizer is not currently available.');
        }
      } catch (err) {
        if (err.response?.status === 404) {
          setError('Visualizer not found. Please check the URL.');
        } else {
          setError('Unable to load visualizer. Please try again later.');
        }
      } finally {
        setLoading(false);
      }
    };
    
    checkAccess();
  }, [shopName]);

  // Get available finishes for selected brand
  const getAvailableFinishes = () => {
    if (!selectedBrand) return [];
    const brandWraps = masterWraps.filter(w => w.manufacturer === selectedBrand);
    const finishes = [...new Set(brandWraps.map(w => w.finish))];
    return finishTypes.filter(f => finishes.includes(f));
  };

  // Get wraps filtered by brand and finish
  const getFilteredWraps = () => {
    if (!selectedBrand || !selectedFinish) return [];
    return masterWraps.filter(w => 
      w.manufacturer === selectedBrand && 
      w.finish === selectedFinish
    );
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }
    const reader = new FileReader();
    reader.onload = (event) => {
      setCarImage(event.target.result);
      setCarImageBase64(event.target.result);
      setStep(2);
    };
    reader.readAsDataURL(file);
  };

  const handleSampleCarSelect = async (car) => {
    try {
      const response = await fetch(car.url);
      const blob = await response.blob();
      const reader = new FileReader();
      reader.onload = (event) => {
        setCarImage(event.target.result);
        setCarImageBase64(event.target.result);
        setStep(2);
      };
      reader.readAsDataURL(blob);
    } catch (error) {
      setCarImage(car.url);
      setCarImageBase64(car.url);
      setStep(2);
    }
  };

  // Handle brand selection
  const handleBrandSelect = (brand) => {
    setSelectedBrand(brand);
    setSelectedFinish(null);
    setWrapSelectionStep(2);
  };

  // Handle finish selection
  const handleFinishSelect = (finish) => {
    setSelectedFinish(finish);
    setWrapSelectionStep(3);
  };

  // Go back in wrap selection
  const handleWrapSelectionBack = () => {
    if (wrapSelectionStep === 3) {
      setSelectedFinish(null);
      setWrapSelectionStep(2);
    } else if (wrapSelectionStep === 2) {
      setSelectedBrand(null);
      setWrapSelectionStep(1);
    }
  };

  const handleWrapSelect = async (wrap) => {
    setSelectedWrap(wrap);
    setGenerating(true);

    try {
      const studioRes = await axios.get(`${API}/studios/public/${shopName}`);
      const studioId = studioRes.data.id;

      const renderRes = await axios.post(`${API}/renders`, {
        studio_id: studioId,
        wrap_id: wrap.id,
        car_image_base64: carImageBase64,
        finish: wrap.finish || 'gloss'
      });

      const renderId = renderRes.data.id;
      await axios.post(`${API}/renders/${renderId}/generate`);

      let attempts = 0;
      const maxAttempts = 60;
      const pollInterval = setInterval(async () => {
        attempts++;
        try {
          const statusRes = await axios.get(`${API}/renders/${renderId}`);
          if (statusRes.data.status === 'completed' && statusRes.data.result_image_url) {
            clearInterval(pollInterval);
            setResultImage(statusRes.data.result_image_url);
            setGenerating(false);
            setStep(3);
          } else if (statusRes.data.status === 'fallback' || attempts >= maxAttempts) {
            clearInterval(pollInterval);
            setGenerating(false);
            toast.error('Generation failed. Please try again.');
          }
        } catch (err) {
          clearInterval(pollInterval);
          setGenerating(false);
          toast.error('Something went wrong');
        }
      }, 2000);
    } catch (err) {
      setGenerating(false);
      toast.error('Failed to start generation');
    }
  };

  const resetVisualizer = () => {
    setStep(1);
    setWrapSelectionStep(1);
    setCarImage(null);
    setCarImageBase64('');
    setSelectedWrap(null);
    setSelectedBrand(null);
    setSelectedFinish(null);
    setResultImage(null);
  };

  const accentColor = studioInfo?.branding?.accent_color || '#FF3399';

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-12 h-12 border-2 border-zinc-800 border-t-[#FF3399] animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 mx-auto mb-6 border border-zinc-800 flex items-center justify-center">
            <AlertCircle className="w-10 h-10 text-zinc-600" />
          </div>
          <h1 className="text-2xl font-black text-white mb-4">Visualizer Unavailable</h1>
          <p className="text-zinc-500 mb-8">{error}</p>
          <Link to="/" className="text-[#FF3399] hover:underline font-mono text-sm tracking-wider">
            LEARN MORE ABOUT WRAPVIZ →
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-[#050505] ${isEmbed ? '' : 'noise-overlay'}`}>
      {/* Header - hide in embed mode */}
      {!isEmbed && (
        <div className="text-center py-8 px-6 border-b border-zinc-800">
          <div className="flex items-center justify-center gap-3 mb-2">
            {studioInfo?.branding?.logo_url ? (
              <img 
                src={studioInfo.branding.logo_url} 
                alt={studioInfo.studio_name} 
                className="h-10"
              />
            ) : (
              <div className="w-10 h-10 flex items-center justify-center" style={{ backgroundColor: accentColor }}>
                <span className="text-black font-black">{studioInfo?.studio_name?.charAt(0) || 'W'}</span>
              </div>
            )}
            <h1 className="text-2xl font-black text-white">
              {studioInfo?.studio_name || 'Wrap Visualizer'}
            </h1>
          </div>
          <p className="text-zinc-500 font-mono text-xs tracking-wider">AI-POWERED WRAP PREVIEW</p>
        </div>
      )}

      {/* Visualizer Container */}
      <div className={`${isEmbed ? 'p-4' : 'py-12 px-6'}`}>
        <div className="max-w-5xl mx-auto">
          {/* Main Container */}
          <div className="relative">
            {/* Glow Effect */}
            <div className="absolute -inset-1 opacity-30 blur-xl"
                 style={{ background: `linear-gradient(135deg, ${accentColor}30 0%, transparent 50%, ${accentColor}30 100%)` }} />
            
            <div className="relative bg-[#09090B] border border-zinc-800">
              {/* Step Indicator */}
              <div className="flex items-center justify-between p-4 border-b border-zinc-800">
                <div className="flex items-center gap-3">
                  {[1, 2, 3].map((s) => (
                    <div key={s} className="flex items-center gap-2">
                      <div 
                        className={`w-8 h-8 flex items-center justify-center font-mono text-sm border-2 transition-all duration-300
                          ${step > s 
                            ? 'border-transparent text-black' 
                            : step === s 
                              ? 'border-current shadow-lg' 
                              : 'border-zinc-700 text-zinc-600'
                          }`}
                        style={{ 
                          backgroundColor: step > s ? accentColor : 'transparent',
                          color: step === s ? accentColor : undefined,
                          borderColor: step === s ? accentColor : undefined,
                          boxShadow: step === s ? `0 0 20px -5px ${accentColor}` : undefined
                        }}
                      >
                        {step > s ? <Check className="w-4 h-4" /> : s}
                      </div>
                      {s < 3 && <div className={`w-6 h-px ${step > s ? '' : 'bg-zinc-800'}`}
                                    style={{ backgroundColor: step > s ? accentColor : undefined }} />}
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-2 text-zinc-600">
                  <Sparkles className="w-4 h-4" style={{ color: accentColor }} />
                  <span className="font-mono text-xs tracking-wider hidden sm:inline">AI POWERED</span>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                {/* Step 1: Upload */}
                {step === 1 && (
                  <div className="text-center py-6">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleFileUpload}
                      className="hidden"
                    />
                    <div 
                      onClick={() => fileInputRef.current?.click()}
                      className="border border-dashed border-zinc-700 p-12 cursor-pointer hover:border-current transition-all group"
                    >
                      <div className="w-16 h-16 mx-auto mb-4 border border-zinc-700 flex items-center justify-center group-hover:border-current transition-colors"
                           style={{ '--hover-color': accentColor }}>
                        <Upload className="w-8 h-8 text-zinc-600 group-hover:text-current transition-colors" />
                      </div>
                      <p className="text-zinc-300 font-medium text-lg mb-2">Upload your car photo</p>
                      <p className="text-sm text-zinc-600">PNG, JPG up to 10MB</p>
                    </div>
                    
                    {sampleCars.length > 0 && (
                      <div className="mt-8">
                        <p className="font-mono text-xs tracking-widest text-zinc-600 mb-4">OR SELECT A SAMPLE</p>
                        <div className="flex justify-center gap-3">
                          {sampleCars.slice(0, 3).map((car, i) => (
                            <img
                              key={i}
                              src={car.url}
                              alt={car.name}
                              onClick={() => handleSampleCarSelect(car)}
                              className="w-24 h-16 object-cover border border-zinc-800 hover:border-current cursor-pointer transition-all"
                              style={{ '--hover-color': accentColor }}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Step 2: Select Wrap - Multi-step: Brand → Finish → Color */}
                {step === 2 && !generating && (
                  <div className="flex gap-6">
                    {/* Left: Car preview - full visibility */}
                    <div className="w-1/2 flex-shrink-0">
                      <p className="font-mono text-xs tracking-widest text-zinc-500 mb-2">YOUR VEHICLE</p>
                      <div className="relative">
                        <img 
                          src={carImage} 
                          alt="Your car" 
                          className="w-full h-[300px] object-cover border border-zinc-800"
                        />
                        {/* Selection breadcrumb */}
                        <div className="absolute bottom-2 left-2 flex items-center gap-1 text-xs">
                          {selectedBrand && (
                            <span className="px-2 py-0.5 border" style={{ backgroundColor: `${accentColor}20`, borderColor: `${accentColor}50`, color: accentColor }}>
                              {selectedBrand}
                            </span>
                          )}
                          {selectedFinish && (
                            <>
                              <ChevronRight className="w-3 h-3 text-zinc-600" />
                              <span className="bg-zinc-800 text-zinc-300 px-2 py-0.5 border border-zinc-700">
                                {selectedFinish}
                              </span>
                            </>
                          )}
                        </div>
                        <button 
                          onClick={resetVisualizer}
                          className="absolute top-2 left-2 text-xs bg-black/60 text-zinc-400 hover:text-white px-2 py-1 flex items-center gap-1"
                        >
                          <ChevronLeft className="w-3 h-3" /> Change
                        </button>
                      </div>
                    </div>

                    {/* Right: Selection panel */}
                    <div className="w-1/2 flex flex-col">
                      {/* Quick Pick - Popular wraps */}
                      {wrapSelectionStep === 1 && masterWraps.length > 0 && (
                        <div className="mb-3">
                          <p className="font-mono text-xs mb-2" style={{ color: accentColor }}>🔥 QUICK PICK</p>
                          <div className="flex gap-2 overflow-x-auto pb-4">
                            {masterWraps.slice(0, 8).map((wrap) => (
                              <button
                                key={wrap.id}
                                onClick={() => handleWrapSelect(wrap)}
                                data-name={wrap.name}
                                className="wrap-swatch flex-shrink-0 w-10 h-10 border-2 border-zinc-700 overflow-hidden"
                              >
                                <img src={wrap.image_url} alt={wrap.name} className="w-full h-full object-cover" />
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Step 2.1: Select Brand */}
                      {wrapSelectionStep === 1 && (
                        <div className="flex-1">
                          <p className="font-mono text-xs tracking-widest text-zinc-500 mb-2">1. SELECT BRAND</p>
                          <div className="grid grid-cols-2 gap-2">
                            {manufacturers.map((mfr) => (
                              <button
                                key={mfr.name}
                                onClick={() => handleBrandSelect(mfr.name)}
                                className="p-3 border border-zinc-800 hover:border-current transition-colors text-left group"
                                style={{ '--hover-color': accentColor }}
                              >
                                <span className="text-sm font-bold text-white group-hover:text-current transition-colors">
                                  {mfr.name}
                                </span>
                                <span className="text-xs text-zinc-600 ml-2">
                                  {mfr.wrap_count}
                                </span>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Step 2.2: Select Finish */}
                      {wrapSelectionStep === 2 && (
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-2">
                            <p className="font-mono text-xs tracking-widest text-zinc-500">2. SELECT FINISH</p>
                            <button 
                              onClick={handleWrapSelectionBack}
                              className="text-xs text-zinc-500 hover:text-current flex items-center gap-1"
                              style={{ '--hover-color': accentColor }}
                            >
                              <ChevronLeft className="w-3 h-3" /> Back
                            </button>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            {getAvailableFinishes().map((finish) => (
                              <button
                                key={finish}
                                onClick={() => handleFinishSelect(finish)}
                                className="p-3 border border-zinc-800 hover:border-current transition-colors text-center group"
                                style={{ '--hover-color': accentColor }}
                              >
                                <span className="text-sm font-medium text-white group-hover:text-current transition-colors">
                                  {finish}
                                </span>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Step 2.3: Select Color */}
                      {wrapSelectionStep === 3 && (
                        <div className="flex-1 flex flex-col">
                          <div className="flex items-center justify-between mb-2">
                            <p className="font-mono text-xs tracking-widest text-zinc-500">3. SELECT COLOR</p>
                            <button 
                              onClick={handleWrapSelectionBack}
                              className="text-xs text-zinc-500 hover:text-current flex items-center gap-1"
                              style={{ '--hover-color': accentColor }}
                            >
                              <ChevronLeft className="w-3 h-3" /> Back
                            </button>
                          </div>
                          <div className="grid grid-cols-4 gap-2 overflow-y-auto flex-1 max-h-[220px] pr-1 pt-6">
                            {getFilteredWraps().map((wrap) => (
                              <button
                                key={wrap.id}
                                onClick={() => handleWrapSelect(wrap)}
                                data-name={wrap.name.replace(selectedBrand || '', '').trim()}
                                className="wrap-swatch relative aspect-square border-2 border-zinc-800 overflow-hidden"
                              >
                                {wrap.image_url ? (
                                  <img 
                                    src={wrap.image_url}
                                    alt={wrap.name}
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <div className="w-full h-full bg-zinc-800" />
                                )}
                              </button>
                            ))}
                          </div>
                          {getFilteredWraps().length === 0 && (
                            <p className="text-xs text-zinc-600 text-center py-4">
                              No wraps found
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Generating State */}
                {generating && (
                  <div className="text-center py-16">
                    <div className="w-20 h-20 mx-auto mb-6 relative">
                      <div className="absolute inset-0 border-2 animate-ping opacity-20"
                           style={{ borderColor: accentColor }} />
                      <div className="absolute inset-0 border-2 border-t-transparent animate-spin"
                           style={{ borderColor: accentColor, borderTopColor: 'transparent' }} />
                    </div>
                    <p className="text-xl font-bold text-white mb-2">Generating your preview</p>
                    <p className="font-mono text-xs tracking-widest text-zinc-500">30-60 SECONDS</p>
                  </div>
                )}

                {/* Step 3: Result */}
                {step === 3 && resultImage && (
                  <div className="text-center">
                    <div className="relative mb-6 inline-block">
                      <img 
                        src={resultImage} 
                        alt="Wrapped car preview" 
                        className="max-w-full max-h-[400px] border border-zinc-800"
                      />
                      <div className="absolute top-3 right-3 px-3 py-1 font-mono text-xs tracking-wider"
                           style={{ backgroundColor: accentColor, color: '#000' }}>
                        AI GENERATED
                      </div>
                    </div>
                    
                    <p className="text-xl font-bold text-white mb-1">
                      Your car in <span style={{ color: accentColor }}>{selectedWrap?.name}</span>
                    </p>
                    <p className="text-zinc-500 text-sm mb-6">{selectedWrap?.finish} finish • {selectedWrap?.manufacturer}</p>
                    
                    <div className="flex justify-center gap-3 flex-wrap">
                      <button
                        onClick={resetVisualizer}
                        className="border border-zinc-700 text-white font-medium px-5 py-2.5 hover:border-white transition-all"
                      >
                        Try Another
                      </button>
                      <button 
                        onClick={() => setShowInquireModal(true)}
                        className="font-bold px-5 py-2.5 transition-all hover:opacity-90 flex items-center gap-2"
                        style={{ backgroundColor: accentColor, color: '#000' }}
                      >
                        <MessageSquare className="w-4 h-4" />
                        Inquire About This Wrap
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Powered by */}
          <p className="text-center mt-6 font-mono text-xs text-zinc-600">
            Powered by <a href="https://wrapviz.ai" target="_blank" rel="noopener noreferrer" className="text-zinc-500 hover:text-white transition-colors">WrapViz</a>
          </p>
        </div>
      </div>

      {/* Inquire Modal */}
      {showInquireModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-[#09090B] border border-zinc-800 max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-white mb-2">Contact {studioDetails?.name || 'Studio'}</h3>
            <p className="text-zinc-500 text-sm mb-6">
              Interested in <span style={{ color: accentColor }}>{selectedWrap?.name}</span>? Get in touch!
            </p>
            
            <div className="space-y-3 mb-6">
              {studioDetails?.phone && (
                <a 
                  href={`tel:${studioDetails.phone}`}
                  className="flex items-center gap-3 p-3 border border-zinc-800 hover:border-zinc-600 transition-colors"
                >
                  <Phone className="w-5 h-5" style={{ color: accentColor }} />
                  <span className="text-white">{studioDetails.phone}</span>
                </a>
              )}
              {studioDetails?.email && (
                <a 
                  href={`mailto:${studioDetails.email}?subject=Wrap Inquiry: ${selectedWrap?.name}&body=Hi, I'm interested in the ${selectedWrap?.name} wrap I previewed on your visualizer.`}
                  className="flex items-center gap-3 p-3 border border-zinc-800 hover:border-zinc-600 transition-colors"
                >
                  <Mail className="w-5 h-5" style={{ color: accentColor }} />
                  <span className="text-white">{studioDetails.email}</span>
                </a>
              )}
              {!studioDetails?.phone && !studioDetails?.email && (
                <p className="text-zinc-500 text-center py-4">Contact information not available</p>
              )}
            </div>
            
            <button 
              onClick={() => setShowInquireModal(false)}
              className="w-full border border-zinc-700 text-zinc-400 py-2 hover:text-white hover:border-zinc-500 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default VisualizerPage;
